const MAX_DEPTH = 10;

/**
 * Checks if a string is likely to be valid JSON (array or object).
 * This is a lightweight check before attempting JSON.parse.
 */
function isLikelyJSON(str: string): boolean {
  const trimmed = str.trim();

  // Must start and end with proper brackets/braces
  const isArrayLike = trimmed.startsWith('[') && trimmed.endsWith(']');
  const isObjectLike = trimmed.startsWith('{') && trimmed.endsWith('}');

  if (!isArrayLike && !isObjectLike) {
    return false;
  }

  // Skip empty arrays/objects (just "[]" or "{}")
  if (trimmed.length === 2) {
    return true; // Let JSON.parse handle empty arrays/objects
  }

  // Must contain quotes or commas (actual JSON content)
  // This filters out "[NOTE]", "{user.name}", etc.
  return trimmed.includes('"') || trimmed.includes(',');
}

/**
 * Type guard to check if a value is a plain object (not an array, null, etc.)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively normalizes object properties to handle cases where arrays or objects
 * are incorrectly double-stringified (e.g., "[\"item1\", \"item2\"]" instead of ["item1", "item2"]).
 * This can happen when the OAS package's sampleFromSchema generates examples.
 *
 * @param obj - The value to normalize
 * @param depth - Current recursion depth (internal use)
 * @returns The normalized value with double-stringified JSON parsed
 */
export default function normalizeObjectProperties(obj: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth >= MAX_DEPTH) {
    return obj;
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => normalizeObjectProperties(item, depth + 1));
  }

  if (isPlainObject(obj)) {
    return Object.entries(obj).reduce((normalized: Record<string, unknown>, [key, value]) => {
      if (typeof value === 'string') {
        // Only attempt to parse if it looks like JSON
        if (isLikelyJSON(value)) {
          try {
            const parsed = JSON.parse(value);
            // Only use parsed value if it's actually an object or array
            // This prevents parsing "true", "123", etc. that happen to be valid JSON
            if (typeof parsed === 'object' && parsed !== null) {
              normalized[key] = normalizeObjectProperties(parsed, depth + 1);
            } else {
              normalized[key] = value;
            }
          } catch {
            // If parsing fails, keep the original string value
            normalized[key] = value;
          }
        } else {
          normalized[key] = value;
        }
      } else if (typeof value === 'object') {
        normalized[key] = normalizeObjectProperties(value, depth + 1);
      } else {
        normalized[key] = value;
      }
      return normalized;
    }, {});
  }

  return obj;
}
